// Phygital · shared design primitives.
// Every functional Phygital flow imports from this file so styles stay consistent.

import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Check, ChevronDown, ChevronRight, Info, Lock, X } from "lucide-react";

// ---------- design tokens ----------
// FRAME: desktop-friendly - 720px on md+, narrows to 420px only on very small screens.
export const TOKENS = {
  FRAME: "w-full max-w-[720px] mx-auto bg-white min-h-screen relative pb-24",
  PAGE: "min-h-screen bg-white",
  INK: "#111111",
  ACCENT: "#7c3aed",
  ACCENT_SOFT: "#f5f3ff",
  ROSE: "#fecdd3",
  MAX_W: 720,
};

export const CATEGORY_COLORS = {
  makeup:  { hex: "#8b5cf6", soft: "#eae2ff", ink: "#4c1d95" },
  lashes:  { hex: "#6bc8ba", soft: "#d5f4ee", ink: "#134e4a" },
  nails:   { hex: "#e08794", soft: "#fbdadd", ink: "#881337" },
  hair:    { hex: "#d94b7a", soft: "#fce7f0", ink: "#831843" },
  style:   { hex: "#a67c52", soft: "#f5ecd8", ink: "#4c2e12" },
  routine: { hex: "#7a7a7a", soft: "#e6e6e6", ink: "#3f3f3f" },
};

// ---------- text ----------
export const SectionLabel = ({ children, testid }) => (
  <p data-testid={testid} className="text-[10px] uppercase tracking-[0.14em] text-stone-500 font-bold px-1">{children}</p>
);
export const HintText = ({ children }) => <p className="text-[10px] text-stone-500 mt-1 leading-relaxed">{children}</p>;

// ---------- tag ----------
export const Tag = ({ children, tone = "stone", size = "sm", testid }) => {
  const tones = {
    stone:   "bg-stone-100 text-stone-700 border-stone-200",
    accent:  "bg-purple-50 text-purple-700 border-purple-200",
    amber:   "bg-amber-50 text-amber-800 border-amber-200",
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
    rose:    "bg-rose-50 text-rose-800 border-rose-200",
    ink:     "bg-stone-900 text-white border-stone-900",
    outline: "bg-white text-stone-700 border-stone-300",
  };
  const sizes = { xs: "text-[9px] px-1.5 py-0.5", sm: "text-[10px] px-2 py-0.5" };
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-1 rounded-full border ${tones[tone] || tones.stone} ${sizes[size]} font-medium tracking-wider uppercase`}>
      {children}
    </span>
  );
};

// ---------- buttons ----------
export const Btn = ({ children, tone = "ink", full, icon: Icon, size = "md", testid, onClick, type = "button", disabled }) => {
  const tones = {
    ink:     "bg-stone-900 text-white hover:bg-black disabled:bg-stone-400",
    outline: "bg-white text-stone-900 border border-stone-300 hover:bg-stone-50",
    accent:  "bg-purple-600 text-white hover:bg-purple-700 disabled:bg-purple-300",
    ghost:   "bg-transparent text-stone-700 hover:bg-stone-100",
    danger:  "bg-white text-rose-700 border border-rose-200 hover:bg-rose-50",
  };
  const sizes = { sm: "text-xs px-3 py-2", md: "text-sm px-4 py-2.5", lg: "text-sm px-5 py-3" };
  return (
    <button type={type} onClick={onClick} data-testid={testid} disabled={disabled}
      className={`rounded-full font-semibold inline-flex items-center justify-center gap-1.5 disabled:cursor-not-allowed ${tones[tone]} ${sizes[size]} ${full ? "w-full" : ""}`}>
      {Icon && <Icon className="w-4 h-4" />}
      {children}
    </button>
  );
};

// ---------- segmented button row ----------
export const SegButton = ({ children, selected, Icon, onClick, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`w-full h-10 min-w-0 rounded-full text-[12px] font-semibold inline-flex items-center justify-center gap-1 px-3 whitespace-nowrap border transition
      ${selected ? "bg-stone-900 text-white border-stone-900" : "bg-white text-stone-700 border-stone-300 hover:border-stone-900"}`}>
    {Icon && <Icon className="w-3.5 h-3.5" />}
    <span className="truncate">{children}</span>
  </button>
);
export const SegRow = ({ children }) => <div className="flex gap-1.5 items-stretch w-full">{children}</div>;

// ---------- pill (single choice / multi choice option) ----------
export const Pill = ({ children, selected, tone = "cream", onClick, testid }) => {
  const tones = {
    cream:  selected ? "bg-stone-900 text-white border-stone-900" : "bg-stone-50 text-stone-700 border-stone-200 hover:border-stone-500",
    accent: selected ? "bg-purple-600 text-white border-purple-600" : "bg-white text-stone-700 border-stone-300 hover:border-purple-600",
    rose:   selected ? "bg-rose-500 text-white border-rose-500" : "bg-rose-50 text-rose-800 border-rose-200 hover:border-rose-500",
  };
  return (
    <button onClick={onClick} data-testid={testid}
      className={`text-[11px] px-3 py-1.5 rounded-full border font-medium transition ${tones[tone] || tones.cream}`}>
      {children}
    </button>
  );
};

// ---------- text input ----------
export const TextInput = ({ value, onChange, placeholder, testid, icon: Icon, type = "text" }) => (
  <div className="rounded-xl border border-stone-300 flex items-center px-3 py-2 focus-within:border-stone-900">
    {Icon && <Icon className="w-3.5 h-3.5 text-stone-500 mr-1.5" />}
    <input type={type} value={value ?? ""} onChange={(e) => onChange?.(e.target.value)}
      placeholder={placeholder} data-testid={testid}
      className="flex-1 bg-transparent text-sm focus:outline-none placeholder:text-stone-400" />
  </div>
);
export const TextArea = ({ value, onChange, placeholder, testid, rows = 3 }) => (
  <textarea value={value ?? ""} onChange={(e) => onChange?.(e.target.value)} placeholder={placeholder} rows={rows}
    data-testid={testid}
    className="w-full rounded-xl border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:border-stone-900 placeholder:text-stone-400" />
);

// ---------- field label ----------
export const FieldLabel = ({ children, right }) => (
  <div className="flex items-center justify-between mb-1.5 px-1">
    <label className="text-[11px] uppercase tracking-wider font-bold text-stone-700">{children}</label>
    {right}
  </div>
);

// ---------- headers, popups ----------
export const Header = ({ title, back, right, sub, testid }) => (
  <header data-testid={testid} className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-stone-200 px-4 h-14 flex items-center justify-between">
    {back
      ? <Link to={back} className="w-8 h-8 -ml-1 flex items-center justify-center text-stone-700 hover:bg-stone-100 rounded-full">
          <ArrowLeft className="w-4 h-4" />
        </Link>
      : <span className="w-8" />}
    <div className="text-center flex-1">
      <p className="text-sm font-bold text-stone-900 leading-tight">{title}</p>
      {sub && <p className="text-[9px] uppercase tracking-wider text-stone-500 mt-0.5">{sub}</p>}
    </div>
    <div className="w-8 flex items-center justify-end">{right}</div>
  </header>
);

// ---------- collapsible row (accordion) ----------
export const Collapsible = ({ label, summary, defaultOpen = false, testid, children, tone = "rose", icon: Icon }) => {
  const [open, setOpen] = React.useState(defaultOpen);
  const bg = tone === "rose" ? "bg-rose-50/40 border-rose-200" : "bg-stone-50 border-stone-200";
  return (
    <div className={`rounded-2xl border ${bg}`} data-testid={testid}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-2 px-3 py-2.5"
        data-testid={testid ? `${testid}-toggle` : undefined}>
        {Icon && <Icon className="w-3.5 h-3.5 text-stone-700" />}
        <span className="text-xs font-bold text-stone-900">{label}</span>
        {summary && !open && <span className="text-[11px] text-stone-500 ml-auto mr-1">{summary}</span>}
        <ChevronDown className={`w-3.5 h-3.5 text-stone-500 transition ${open ? "rotate-180" : ""} ${summary && !open ? "" : "ml-auto"}`} />
      </button>
      {open && <div className="px-3 pb-3">{children}</div>}
    </div>
  );
};

// ---------- swatch / vein-half (canvas helpers) ----------
export const Swatch = ({ hex, selected, onClick, testid, label }) => (
  <button onClick={onClick} data-testid={testid}
    className={`w-9 h-9 rounded-full border-2 transition inline-flex items-center justify-center
      ${selected ? "border-stone-900 ring-2 ring-offset-1 ring-stone-900" : "border-stone-200 hover:border-stone-500"}`}
    style={{ backgroundColor: hex }} title={label}>
    {selected && <Check className="w-3.5 h-3.5 text-white drop-shadow" />}
  </button>
);

export const VeinHalf = ({ top, bottom, selected, onClick, testid, label }) => (
  <button onClick={onClick} data-testid={testid}
    className={`w-16 h-9 rounded-full overflow-hidden border-2 transition
      ${selected ? "border-stone-900 ring-2 ring-offset-1 ring-stone-900" : "border-stone-200 hover:border-stone-500"}`}
    title={label}>
    <div className="h-1/2 w-full" style={{ backgroundColor: top }} />
    <div className="h-1/2 w-full" style={{ backgroundColor: bottom }} />
  </button>
);

// ---------- tile (image-forward option) ----------
export const OptionTile = ({ label, selected, onClick, hint, testid, visual }) => (
  <button onClick={onClick} data-testid={testid}
    className={`rounded-2xl p-2 border transition text-left
      ${selected ? "border-stone-900 bg-stone-900 text-white" : "border-stone-200 bg-white text-stone-800 hover:border-stone-900"}`}>
    <div className={`aspect-square rounded-xl mb-1.5 flex items-center justify-center ${selected ? "bg-white/10" : "bg-stone-50"}`}>
      {visual}
    </div>
    <p className="text-[11px] font-semibold text-center truncate">{label}</p>
    {hint && <p className="text-[9px] text-center opacity-70 truncate">{hint}</p>}
  </button>
);

// ---------- host row ----------
export const HostRow = ({ name, brand, selected, onClick, testid, right }) => (
  <button onClick={onClick} data-testid={testid}
    className={`w-full rounded-xl border px-3 py-2 flex items-center gap-2 transition
      ${selected ? "border-stone-900 bg-stone-900 text-white" : "border-stone-200 bg-white text-stone-800 hover:border-stone-900"}`}>
    <span className={`w-6 h-6 rounded-full ${selected ? "bg-white/20" : "bg-stone-100"} inline-flex items-center justify-center text-[10px] font-bold`}>
      {(name || "?").charAt(0)}
    </span>
    <span className="text-xs font-semibold flex-1 truncate text-left">
      {name}
      {brand && <span className={`ml-1 text-[10px] font-normal ${selected ? "text-white/70" : "text-stone-500"}`}>· {brand}</span>}
    </span>
    {right}
  </button>
);

// ---------- badge / lock ----------
export const LockBadge = ({ children = "Private" }) => (
  <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-bold text-rose-700 bg-rose-50 border border-rose-200 rounded-full px-1.5 py-0.5">
    <Lock className="w-2.5 h-2.5" /> {children}
  </span>
);

// ---------- inline note ----------
export const InlineNote = ({ tone = "info", children, icon: Icon = Info }) => {
  const map = {
    info:    "bg-purple-50 text-purple-800 border-purple-200",
    warn:    "bg-amber-50 text-amber-800 border-amber-200",
    success: "bg-emerald-50 text-emerald-800 border-emerald-200",
  };
  return (
    <div className={`rounded-xl border px-3 py-2 flex items-start gap-1.5 ${map[tone] || map.info}`}>
      <Icon className="w-3.5 h-3.5 mt-0.5 shrink-0" />
      <p className="text-[11px] leading-relaxed">{children}</p>
    </div>
  );
};

// ---------- close (top right of modals) ----------
export const CloseButton = ({ onClick, to, testid }) => {
  const cls = "w-8 h-8 rounded-full bg-stone-100 hover:bg-stone-200 flex items-center justify-center";
  if (to) return <Link to={to} className={cls} data-testid={testid}><X className="w-4 h-4 text-stone-700" /></Link>;
  return <button onClick={onClick} className={cls} data-testid={testid}><X className="w-4 h-4 text-stone-700" /></button>;
};

// ---------- disclosure row ("open something") ----------
export const DisclosureRow = ({ label, value, onClick, testid, muted }) => (
  <button onClick={onClick} data-testid={testid}
    className="w-full flex items-center justify-between py-2.5 border-b border-stone-100 hover:bg-stone-50 px-1 -mx-1 rounded">
    <div className="min-w-0 text-left">
      <p className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">{label}</p>
      <p className={`text-sm ${muted ? "text-stone-400 italic" : "text-stone-900"} truncate`}>{value}</p>
    </div>
    <ChevronRight className="w-4 h-4 text-stone-400 shrink-0 ml-2" />
  </button>
);

// ---------- visibility selector (Private / Host / Public) ----------
// Compact 3-way segmented used in each preference row.
export const VisibilitySelect = ({ value = "private", onChange, testid }) => {
  const opts = [
    { id: "private", label: "Only me" },
    { id: "host",    label: "My hosts" },
    { id: "public",  label: "Public link" },
  ];
  return (
    <div className="inline-flex rounded-full border border-stone-200 bg-white p-0.5" data-testid={testid}>
      {opts.map(o => (
        <button key={o.id} type="button"
          data-testid={testid ? `${testid}-${o.id}` : undefined}
          onClick={() => onChange?.(o.id)}
          className={`px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded-full transition
            ${value === o.id
              ? "bg-stone-900 text-white"
              : "text-stone-500 hover:text-stone-900"}`}>
          {o.label}
        </button>
      ))}
    </div>
  );
};
